import { I18n } from "@assets/strings";
import React, { useState } from "react";
import { ClipLoader } from "react-spinners";
import styled from "styled-components";
import { useServices } from "@core/servicesContext";
import { useOrganizationAlias } from "@domain/currentProfile/hooks";
import { useAccreditations } from "@domain/organization/hooks";
import { Organization } from "@domain/organization/organization";
import { useIsMobile } from "@utils/hooks";
import { RoundedPlusButton } from "@ui/components/buttons/roundedPlusButton";
import { Description } from "@ui/components/containers/description";
import { DraftRenderer } from "@ui/components/containers/draftRenderer";
import { PrimaryBold } from "@ui/components/containers/primary";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { Row, row, Spacer, Stack, stack } from "@ui/components/layout";
import { KebabMenu } from "@ui/components/menus/kebabMenu";
import { SimpleMenuContent } from "@ui/components/menus/simpleMenuContent";
import { useModalTools } from "@ui/modals/modalContext";
import { theme } from "@ui/styles/theme";
import { desktop, gap, mobile } from "@ui/styles/utils";
import { createToast } from "@ui/toasts/utils";
import { ShareroomBlocIcons } from "@ui/pages/group/shareroom/shareroomInfo";
import { AccreditationEditorModal } from "./accreditationEditorModal";
import { AccreditationOrderModal } from "./accreditationOrderModal";

interface OrganizationAccreditationsProps extends React.HTMLAttributes<HTMLDivElement> {
	organization: Organization;
}

export const OrganizationAccreditations: React.FC<OrganizationAccreditationsProps> = ({
	organization,
	...htmlProps
}) => {
	const alias = useOrganizationAlias();
	const { result: accreditations, loading } = useAccreditations(organization.id);
	const empty = !accreditations || accreditations.length < 1;
	const { createModal } = useModalTools();
	const { organizationService } = useServices();
	const [itemHovered, setItemHovered] = useState<string | null>(null);
	const isMobile = useIsMobile();

	// if (!loading && empty && !alias) {
	// 	return null;
	// }

	return (
		<Container {...htmlProps}>
			<TitleContainer>
				<StyledPrimarybold>
					{organization.type === "COMPANY"
						? I18n.t("organization.distinction.title")
						: I18n.t("organization.accreditations.title")}
				</StyledPrimarybold>
				{!!alias && (
					<Row gap={8}>
						<RoundedPlusButton
							onClick={() =>
								createModal((close) => <AccreditationEditorModal close={close} organization={organization} />)
							}
						/>
						<RoundedPlusButton
							label={I18n.t("sort")}
							onClick={() =>
								createModal((close) => (
									<AccreditationOrderModal close={close} organization={organization} accreditations={accreditations} />
								))
							}
						/>
					</Row>
				)}
			</TitleContainer>
			{loading ? (
				<ClipLoader />
			) : empty ? (
				<Stack style={{ width: "100%" }} gap={10} align="center" justify="center">
					<IconView>
						<img src={ShareroomBlocIcons["presentation"]} />
					</IconView>

					<Description>
						{I18n.t(`organization.accreditations.${"presentation"}.${alias ? "owner" : "member"}.description`)}
					</Description>
				</Stack>
			) : (
				// !!alias && (
				// 	<>
				// 		<Secondary>{I18n.t("organization.accreditations.empty")}</Secondary>
				// 		<PrimaryButton
				// 			onClick={() =>
				// 				createModal((close) => <AccreditationEditorModal close={close} organization={organization} />)
				// 			}
				// 		>
				// 			{I18n.t("organization.accreditations.new")}
				// 		</PrimaryButton>
				// 	</>
				// )
				<Accreditations>
					{accreditations?.map((acc) => (
						<AccreditationView
							key={acc.id}
							onMouseEnter={() => setItemHovered(acc.id)}
							onMouseLeave={() => setItemHovered(null)}
						>
							<LogoWrapper>
								<img src={acc.logo.url} />
								{acc.date && (
									<AccreditationDate>
										{I18n.t("organization.accreditation.date.view", { year: acc.date.getFullYear() })}
									</AccreditationDate>
								)}
							</LogoWrapper>
							<div>
								<Title>{acc.name}</Title>
								{acc.description.getPlainText() ? <StyledDraftDescription content={acc.description} /> : null}
								<Reference>
									{I18n.t("organization.accreditation.reference.view", { reference: acc.referenceNumber })}
								</Reference>
							</div>
							<Spacer />
							{alias && (
								<KebabMenu isVisible={isMobile ? true : itemHovered === acc.id}>
									<SimpleMenuContent
										onEdit={() =>
											createModal((close) => (
												<AccreditationEditorModal
													close={close}
													organization={organization}
													initialAccreditation={acc}
												/>
											))
										}
										onDelete={() =>
											organizationService
												.deleteAccreditation(organization.id, acc.id)
												.then(() =>
													createToast(
														I18n.t("success"),
														organization.type === "COMPANY"
															? I18n.t("organization.distinction.delete.success")
															: I18n.t("organization.accreditation.delete.success"),
														"success"
													)
												)
										}
									/>
								</KebabMenu>
							)}
						</AccreditationView>
					))}
				</Accreditations>
			)}
		</Container>
	);
};

const Container = styled(RoundedBlock)`
	padding: 50px;
	${gap(70)};
	@media ${mobile} {
		padding: 20px;
	}
`;

const TitleContainer = styled.div`
	${row(20, "space-between", "center")};
`;

const Title = styled.div`
	${({ theme }) => theme.textStyles.title};
`;

const StyledPrimarybold = styled(PrimaryBold)`
	color: ${({ theme }) => theme.colors.secondary};
	font-size: 1.5rem;
	font-weight: 800;

	@media ${mobile} {
		font-size: 0.813rem;
	}
`;

const Accreditations = styled.div`
	${gap(45)};
`;

const AccreditationView = styled.div`
	${row(70, "flex-start", "center")};
	@media ${mobile} {
		${row(20, "flex-start", "center")};
	}
`;

const LogoWrapper = styled.div`
	width: 160px;
	@media ${mobile} {
		width: 80px;
	}
	flex-shrink: 0;
	${stack(10, "center", "center")};

	> img {
		width: 100px;
		height: 100px;
		border-radius: 50%;
		@media ${mobile} {
			max-height: 60px;
		}
	}
`;

const StyledDraftDescription = styled(DraftRenderer)`
	${({ theme }) => theme.textStyles.primary};
	font-weight: 400;
	margin-top: 15px;
	margin-bottom: 10px;
`;

const AccreditationDate = styled.div`
	${({ theme }) => theme.textStyles.text};
	color: ${theme.colors.secondary};
	text-align: center;
`;

const Reference = styled.div`
	${({ theme }) => theme.textStyles.label};
`;

const IconView = styled.div`
	${row(0, "center", "center")}
	width: 100%;
	height: 100%;
	> img {
		@media ${desktop} {
			width: 347px;
			height: 217px;
		}
	}
`;
